/**
 * Signal Colors Utility
 * Manages custom colors for LONG and SHORT signal badges
 * Stores preferences in localStorage
 */

const STORAGE_KEY = "signal_colors";

export interface SignalColors {
  longColor: string; // Start color for LONG gradient
  longColorEnd: string; // End color for LONG gradient
  shortColor: string; // Start color for SHORT gradient
  shortColorEnd: string; // End color for SHORT gradient
}

export const DEFAULT_COLORS: SignalColors = {
  longColor: "#06E363", // Glacial Green start
  longColorEnd: "#AAFAB1", // Glacial Green end
  shortColor: "#E00101", // Glacial Red start
  shortColorEnd: "#FE4244", // Glacial Red end
};

/**
 * Get signal colors from localStorage or return defaults
 */
export const getSignalColors = (): SignalColors => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that all required colors are present
      if (
        parsed.longColor &&
        parsed.longColorEnd &&
        parsed.shortColor &&
        parsed.shortColorEnd
      ) {
        return parsed as SignalColors;
      }
    }
  } catch (error) {
    console.error("Error reading signal colors from localStorage:", error);
  }
  return DEFAULT_COLORS;
};

/**
 * Save signal colors to localStorage
 */
export const saveSignalColors = (colors: SignalColors): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
  } catch (error) {
    console.error("Error saving signal colors to localStorage:", error);
    throw new Error("Failed to save signal colors");
  }
};

/**
 * Reset signal colors to defaults
 */
export const resetSignalColors = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Error resetting signal colors:", error);
  }
};

/**
 * Get gradient string for LONG signal
 */
export const getLongGradient = (): string => {
  const colors = getSignalColors();
  return `linear-gradient(135deg, ${colors.longColor}, ${colors.longColorEnd})`;
};

/**
 * Get gradient string for SHORT signal
 */
export const getShortGradient = (): string => {
  const colors = getSignalColors();
  return `linear-gradient(135deg, ${colors.shortColor}, ${colors.shortColorEnd})`;
};

/**
 * Get start color for LONG signal (for single color uses)
 */
export const getLongStartColor = (): string => {
  return getSignalColors().longColor;
};

/**
 * Get start color for SHORT signal (for single color uses)
 */
export const getShortStartColor = (): string => {
  return getSignalColors().shortColor;
};

